/**
 * useOrgChartEmployees Hook
 * V137.5: 조직도 전용 직원 목록 조회
 *
 * 조직도 페이지에서 사용하는 최소 직원 정보를 조회합니다.
 * HR/Admin 권한 없이도 로그인 사용자가 조직도를 볼 수 있습니다.
 *
 * **반환 필드 (민감정보 제외):**
 * - id, nameKo, email, orgUnitId, orgUnitName
 * - positionTitle, positionId, jobTitle, jobTitleId
 * - firstHireDate, ngleFirstHireDate, status
 *
 * **사용 예시:**
 * ```tsx
 * function OrgChartPage() {
 *   const { employees, isLoading, error } = useOrgChartEmployees();
 *
 *   // 조직별 구성원 필터링
 *   const teamMembers = employees.filter(emp => emp.orgUnitId === 'team_a');
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * 조직도 직원 정보 타입 (최소 필드)
 */
export interface OrgChartEmployee {
  id: string;
  nameKo: string;
  email: string;
  orgUnitId: string;
  orgUnitName: string;
  positionTitle: string;
  positionId: string;
  jobTitle: string;
  jobTitleId: string;
  firstHireDate: string | null;
  ngleFirstHireDate: string | null;
  status: string;
}

/**
 * 백엔드 응답 타입
 */
interface GetOrgChartEmployeesResponse {
  success: boolean;
  employees: OrgChartEmployee[];
  count: number;
}

/**
 * useOrgChartEmployees 반환 타입
 */
export interface UseOrgChartEmployeesResult {
  /** 직원 목록 */
  employees: OrgChartEmployee[];

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;
}

/**
 * useOrgChartEmployees Hook
 *
 * 조직도 표시에 필요한 최소 직원 정보를 조회합니다.
 * 로그인 사용자라면 누구나 조회 가능합니다.
 */
export function useOrgChartEmployees(): UseOrgChartEmployeesResult {
  const [employees, setEmployees] = useState<OrgChartEmployee[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setEmployees([]);
      setIsLoading(false);
      return;
    }

    const fetchEmployees = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const fn = httpsCallable<Record<string, never>, GetOrgChartEmployeesResponse>(
          functions!,
          'getOrgChartEmployees'
        );

        const result = await fn({});

        if (result.data.success) {
          setEmployees(result.data.employees);
        } else {
          throw new Error('조직도 직원 목록 조회 실패');
        }
      } catch (err: any) {
        console.error('[useOrgChartEmployees] 오류:', err);
        setError(err);
        setEmployees([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  return { employees, isLoading, error };
}
